import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useUser, UserFetchOutcome } from './UserContext';
import { Entity, User } from '../types/apiTypes';

import {
  safeGetItem,
  safeSessionGetItem,
  safeSessionRemoveItem,
  safeSessionSetItem,
  safeSetItem,
} from '../shared/safeStorage';

export const useAuthRouter = () => {
  const { isAuthenticated, isLoadingCached } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Try to use UserContext, but handle if it's not available
  let userRecord: User | null = null;
  let userRole: Entity | null = null;
  let isLoadingUser = false;
  let userFetchOutcome: UserFetchOutcome = 'unknown';
  try {
    const userContext = useUser();
    userRecord = userContext.userRecord;
    userRole = userContext.userRole;
    isLoadingUser = userContext.isLoadingUser;
    userFetchOutcome = userContext.userFetchOutcome;
  } catch (error) {
    // UserContext not available, which is fine for some routes
  }

  useEffect(() => {
    // Wait for all loading to complete
    if (isLoadingCached || isLoadingUser) {
      return;
    }

    const currentPath = location.pathname;

    // Step 1: Handle unauthenticated users
    if (!isAuthenticated) {
      // If on a protected page, redirect to login
      if (currentPath !== '/login' && currentPath !== '/role') {
        // Save the full path including search params and hash
        const fullPath = currentPath + location.search + location.hash;
        safeSessionSetItem('redirectPath', fullPath);

        // Extract and save tachref parameter if present
        const searchParams = new URLSearchParams(location.search);
        const tachref = searchParams.get('tachref');
        if (tachref) {
          safeSetItem('tachref', tachref);
        }

        // Also try localStorage for incognito persistence
        // try {
        //   localStorage.setItem('auth0_redirect_path', fullPath);
        // } catch (e) {
        //   // localStorage might fail in some incognito modes
        // }
        // Also pass as URL param to login page
        navigate(`/login?returnTo=${encodeURIComponent(fullPath)}`);
      }
      return;
    }

    // Step 2: Handle authenticated users based on user record status
    // If authenticated but no user record (404 from API)
    // IMPORTANT: Only redirect to /role if we're sure the user doesn't exist
    // UserContext sets userRecord to null explicitly when user doesn't exist
    if (!userRecord && !isLoadingUser) {
      // Only route a record-less user into the onboarding/role flow when the
      // lookup DEFINITIVELY found no account ('absent'). On a transient fetch
      // error the record is null but the user may well be active/pre_active —
      // sending them to /role here is exactly the reported bug. Wait instead;
      // UserContext retries the lookup.
      if (userFetchOutcome !== 'absent') {
        return;
      }

      // Special case: If we're on /signup and have a saved role, don't redirect
      // This handles the case where user just created an account and UserContext is still loading
      const savedRole = safeGetItem('tachUserRole');
      if (currentPath.includes('/signup') && savedRole) {
        // Wait for UserContext to load the newly created user
        return;
      }
      // Check if this is a driver signup flow
      const driverInviteId = safeSessionGetItem('driver_invite_id');
      const partnerAgentInviteId = safeSessionGetItem(
        'partner_agent_invite_id'
      );
      const redirectPath = safeSessionGetItem('redirectPath');

      // If this is a driver signup flow, redirect to driver-signup page
      if (driverInviteId || redirectPath?.includes('/driver-signup')) {
        if (currentPath !== '/driver-signup') {
          navigate('/driver-signup');
        }
        return;
      }

      // If this is a partner agent signup flow, redirect to partner-agent-signup page
      if (
        partnerAgentInviteId ||
        redirectPath?.includes('/partner-agent-signup')
      ) {
        if (currentPath !== '/partner-agent-signup') {
          navigate('/partner-agent-signup');
        }
        return;
      }

      // Not a special signup flow - proceed with normal role selection
      if (currentPath !== '/role') {
        // Save the current path before redirecting to /role
        if (currentPath.startsWith('/dashboard/')) {
          const fullPath = currentPath + location.search + location.hash;
          safeSessionSetItem('redirectPath', fullPath);
          // Also pass as URL param to role page
          navigate(`/role?returnTo=${encodeURIComponent(fullPath)}`);
        } else {
          navigate('/role');
        }
      }
      return;
    }

    // Step 3: Handle authenticated users with user record
    // At this point we know userRecord exists because we checked above
    if (!userRecord) return;

    switch (userRecord.status) {
      case 'pending':
        // Pending users always go to signup - no deep links allowed
        if (!currentPath.includes('/signup')) {
          // Clear any saved redirect path - pending users shouldn't access it
          safeSessionRemoveItem('redirectPath');
          navigate('/signup');
        }
        break;

      case 'pre_active':
        // Pre-active users (with demo data) should not be on login, role, or signup pages
        if (
          currentPath === '/login' ||
          currentPath === '/role' ||
          currentPath.includes('signup')
        ) {
          // Always redirect pre-active customers to home to see their demo data
          navigate('/dashboard/home');
        }
        break;

      case 'active':
        // Active users should not be on login, role, or signup pages
        if (
          currentPath === '/login' ||
          currentPath === '/role' ||
          currentPath.includes('signup')
        ) {
          // Check for redirect path from URL param, fallback to sessionStorage
          const searchParams = new URLSearchParams(location.search);
          const returnToParam = searchParams.get('returnTo');
          const redirectPath =
            returnToParam || safeSessionGetItem('redirectPath');

          if (
            redirectPath &&
            !redirectPath.includes('/login') &&
            !redirectPath.includes('signup') &&
            !redirectPath.includes('/role') &&
            redirectPath.includes('/dashboard')
          ) {
            safeSessionRemoveItem('redirectPath');
            navigate(redirectPath);
          } else if (userRecord.userType === Entity.Partner) {
            navigate('/dashboard/referrals/overview');
          } else {
            navigate('/dashboard/home');
          }
        }
        break;

      case 'terminated':
        if (currentPath !== '/terminated') {
          navigate('/terminated');
        }
        break;
    }
  }, [
    isAuthenticated,
    isLoadingCached,
    isLoadingUser,
    userRecord,
    userFetchOutcome,
    location,
    navigate,
  ]);

  return {
    isLoading: isLoadingCached || isLoadingUser,
    isAuthenticated,
    userRecord,
    userRole,
  };
};
